import React, { useContext, useEffect, useState } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
} from "react-bootstrap";
import { FaHome, FaLocationArrow } from "react-icons/fa";
import api from "../api";
import { AuthContext } from "../context/AuthContext";

const defaultRoomForm = (user) => ({
  title: "",
  description: "",
  state: user?.state || "",
  city: user?.city || "",
  location: "",
  price: "",
  beds: "",
  baths: "",
  area: "",
  image: "",
  contactEmail: user?.email || "",
  contactPhone: user?.phone || "",
  latitude: "",
  longitude: "",
});

export default function PostRoom() {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState(defaultRoomForm(user));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationMessage, setLocationMessage] = useState("");

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      state: user?.state || "",
      city: user?.city || "",
      contactEmail: user?.email || "",
      contactPhone: user?.phone || "",
    }));
  }, [user]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getCurrentPosition = () =>
    new Promise((resolve, reject) => {
      if (!navigator?.geolocation) {
        reject(new Error("Geolocation is not supported in this browser."));
        return;
      }

      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5 * 60 * 1000,
      });
    });

  const handleUseCurrentLocation = async () => {
    setError("");
    setSuccess("");
    setLocationMessage("");
    setGettingLocation(true);

    try {
      const position = await getCurrentPosition();
      const { latitude, longitude } = position.coords || {};

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        setError("Unable to determine your current coordinates.");
        return;
      }

      const latString = latitude.toFixed(6);
      const lonString = longitude.toFixed(6);

      setFormData((prev) => ({
        ...prev,
        latitude: latString,
        longitude: lonString,
      }));
      setLocationMessage(`Captured location: ${latString}, ${lonString}`);
    } catch (geoError) {
      if (geoError?.code === 1) {
        setError(
          "Location permission denied. Please allow access to capture coordinates."
        );
      } else if (geoError?.code === 2) {
        setError(
          "Your location is currently unavailable. Try again in a moment."
        );
      } else if (geoError?.code === 3) {
        setError("Locating took too long. Please try again.");
      } else {
        setError(geoError?.message || "Unable to capture your location.");
      }
    } finally {
      setGettingLocation(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setLocationMessage("");

    if (!user?.email) {
      setError("You need to be logged in to publish a room.");
      return;
    }

    if (
      !formData.title.trim() ||
      !formData.price ||
      !formData.beds ||
      !formData.baths
    ) {
      setError("Title, price, beds, and baths are required.");
      return;
    }

    const latitudeValue =
      formData.latitude !== "" ? Number(formData.latitude) : undefined;
    const longitudeValue =
      formData.longitude !== "" ? Number(formData.longitude) : undefined;

    if (
      (latitudeValue !== undefined && longitudeValue === undefined) ||
      (latitudeValue === undefined && longitudeValue !== undefined)
    ) {
      setError(
        "Please provide both latitude and longitude or leave both empty."
      );
      return;
    }

    if (latitudeValue !== undefined) {
      if (
        !Number.isFinite(latitudeValue) ||
        latitudeValue < -90 ||
        latitudeValue > 90
      ) {
        setError("Latitude must be a number between -90 and 90.");
        return;
      }
    }

    if (longitudeValue !== undefined) {
      if (
        !Number.isFinite(longitudeValue) ||
        longitudeValue < -180 ||
        longitudeValue > 180
      ) {
        setError("Longitude must be a number between -180 and 180.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        ownerEmail: user.email,
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        state: formData.state.trim() || undefined,
        city: formData.city.trim() || undefined,
        location: formData.location.trim() || undefined,
        price: Number(formData.price),
        beds: Number(formData.beds),
        baths: Number(formData.baths),
        area: formData.area !== "" ? Number(formData.area) : undefined,
        image: formData.image.trim() || undefined,
        contactEmail: formData.contactEmail.trim() || undefined,
        contactPhone: formData.contactPhone.trim() || undefined,
        latitude: latitudeValue,
        longitude: longitudeValue,
      };

      const res = await api.post("/rooms", payload);
      if (res.data?.success) {
        setSuccess("Room listing published successfully.");
        setFormData(defaultRoomForm(user));
        setLocationMessage("");
      } else {
        setError(res.data?.message || "Failed to publish the room listing.");
      }
    } catch (err) {
      console.error("Room publish error:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to publish the room listing."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="py-4">
      <Card
        className="shadow border-0 rounded-4 mx-auto"
        style={{ maxWidth: 720 }}
      >
        <Card.Body className="p-4">
          <div className="d-flex align-items-center mb-3">
            <FaHome className="text-primary me-2" size={22} />
            <h4 className="mb-0">Post a Room Listing</h4>
          </div>
          <p className="text-muted mb-4">
            Enter details about the room you want to rent out so nearby tenants
            can discover it.
          </p>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                name="title"
                value={formData.title}
                onChange={updateField}
                placeholder="e.g. Spacious 1 BHK near MG Road"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={updateField}
                placeholder="Highlight amenities, furnishings, or rules"
              />
            </Form.Group>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    name="state"
                    value={formData.state}
                    onChange={updateField}
                    placeholder="State"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    name="city"
                    value={formData.city}
                    onChange={updateField}
                    placeholder="City"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3 mt-3">
              <Form.Label>Locality or landmark</Form.Label>
              <Form.Control
                name="location"
                value={formData.location}
                onChange={updateField}
                placeholder="Neighborhood or nearby landmark"
              />
            </Form.Group>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Monthly rent (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    name="price"
                    value={formData.price}
                    onChange={updateField}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Beds</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    name="beds"
                    value={formData.beds}
                    onChange={updateField}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Baths</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    name="baths"
                    value={formData.baths}
                    onChange={updateField}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mt-1">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Area (sq. ft.)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    name="area"
                    value={formData.area}
                    onChange={updateField}
                    placeholder="Optional"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Image URL</Form.Label>
                  <Form.Control
                    name="image"
                    value={formData.image}
                    onChange={updateField}
                    placeholder="https://example.com/room.jpg"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mt-1">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Latitude</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    name="latitude"
                    value={formData.latitude}
                    onChange={updateField}
                    placeholder="e.g. 12.9716"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Longitude</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    name="longitude"
                    value={formData.longitude}
                    onChange={updateField}
                    placeholder="e.g. 77.5946"
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-2 mt-2">
              <Button
                type="button"
                variant="outline-primary"
                onClick={handleUseCurrentLocation}
                disabled={gettingLocation}
                className="d-inline-flex align-items-center gap-2"
              >
                {gettingLocation ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    <span>Capturing location...</span>
                  </>
                ) : (
                  <>
                    <FaLocationArrow /> Use my current location
                  </>
                )}
              </Button>
              {locationMessage && (
                <div className="text-success small">{locationMessage}</div>
              )}
            </div>
            <Form.Text className="text-muted">
              Adding coordinates makes your listing appear in nearby searches.
            </Form.Text>
            <Row className="g-3 mt-1">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Contact email</Form.Label>
                  <Form.Control
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={updateField}
                    placeholder="contact@example.com"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Contact phone</Form.Label>
                  <Form.Control
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={updateField}
                    placeholder="Phone number"
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-grid mt-4">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Publish room"
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}
